/**
 * Values the operator tunes from the console, defined once and read by both sides.
 *
 * The same arrangement as src/shared/profile-fields.ts: one definition feeds the zod schema that
 * validates a write, the form that offers it, and the label a human reads, so the three cannot
 * drift apart. A key's default lives here rather than in the database, which is why the `setting`
 * table ships empty and why deleting a row is the reset.
 *
 * What does not belong here: anything whose wrong value breaks the runtime rather than merely
 * tuning it. `PASSWORD_ITERATIONS` is the worked example -- workerd refuses to derive above
 * 100,000 on every plan, so a typo in a text box would turn every sign-in into a 500.
 */

export type SettingType = 'integer' | 'boolean';

export interface SettingDefinition {
  readonly key: string;
  readonly type: SettingType;
  readonly default: number | boolean;
  /** Integers only. Both inclusive, and both enforced on write. */
  readonly min?: number;
  readonly max?: number;
  readonly group: string;
  readonly label: string;
  readonly hint: string;
  /**
   * Whether the browser may read it from the public settings endpoint. Everything that only
   * shapes a back-office report stays false: a visitor has no use for it and no business
   * knowing how the chase queue is tuned.
   */
  readonly public: boolean;
}

export const SETTING_GROUPS = [
  { id: 'billing', label: 'Payments', desc: 'When an account starts asking to be chased.' },
  { id: 'reports', label: 'Reports', desc: 'The windows the back-office lists are built from.' },
  { id: 'feed', label: 'Feed and analytics', desc: 'How the grid pages, and what counts as somebody having looked.' },
  { id: 'content', label: 'Limits', desc: 'How much of each thing an account may hold.' },
  {
    id: 'boost',
    label: 'Boost',
    desc:
      'The daily allowance and when it resets. Both have consequences a setting cannot undo on ' +
      'its own -- read the warning before changing either.',
  },
  {
    id: 'punters',
    label: 'Punters and lists',
    desc:
      'The public accounts: who may register and sign in, saved lists and sharing, and the mail ' +
      'that verifies an address and resets a password. A per-punter override in the account ' +
      'editor can deny what is on here, never allow what is off.',
  },
] as const;

export const SETTINGS = [
  {
    key: 'billing.due_soon_days',
    type: 'integer',
    default: 5,
    min: 1,
    max: 90,
    group: 'billing',
    label: 'Show as due this many days ahead',
    hint: 'An account appears on Payments due once its date is this close.',
    public: false,
  },
  {
    key: 'billing.overdue_on_due_date',
    type: 'boolean',
    default: true,
    group: 'billing',
    label: 'The due date itself counts as overdue',
    hint: 'On means an account is overdue on the day its date falls, not the day after.',
    public: false,
  },
  {
    key: 'reports.dormant_days',
    type: 'integer',
    default: 30,
    min: 1,
    max: 365,
    group: 'reports',
    label: 'Dormant after this many days',
    hint: 'Nothing from the account -- no sign-in, no change to the ad -- for this long.',
    public: false,
  },
  {
    key: 'reports.boost_unused_days',
    type: 'integer',
    default: 7,
    min: 1,
    max: 90,
    group: 'reports',
    label: 'Boost counts as unused after',
    hint: 'A live, verified account that has not spent a boost in this many days.',
    public: false,
  },
  {
    key: 'reports.new_account_days',
    type: 'integer',
    default: 14,
    min: 1,
    max: 365,
    group: 'reports',
    label: 'New accounts window',
    hint: 'How far back the new-accounts report reaches.',
    public: false,
  },
  {
    key: 'analytics.dwell_floor_ms',
    type: 'integer',
    default: 1500,
    min: 100,
    max: 10_000,
    group: 'feed',
    label: 'A card counts as seen after',
    hint: 'Milliseconds on screen. Anything shorter is discarded: a card that flashed past says nothing.',
    public: true,
  },
  {
    key: 'analytics.batch_size',
    type: 'integer',
    default: 20,
    min: 1,
    max: 50,
    group: 'feed',
    label: 'Send events in batches of',
    hint: 'Events queue in the browser until this many, or the interval below, whichever comes first.',
    public: true,
  },
  {
    key: 'analytics.flush_interval_ms',
    type: 'integer',
    default: 5000,
    min: 1000,
    max: 60_000,
    group: 'feed',
    label: 'Or after this long',
    hint: 'Milliseconds. Leaving the page always sends whatever is queued, whatever this says.',
    public: true,
  },
  {
    key: 'analytics.max_batch',
    type: 'integer',
    default: 50,
    min: 1,
    max: 50,
    group: 'feed',
    label: 'Accept at most this many per request',
    hint: 'The server-side ceiling. It cannot exceed 50: the request schema refuses a longer body first.',
    public: false,
  },
  {
    key: 'feed.page_size',
    type: 'integer',
    default: 20,
    min: 6,
    max: 60,
    group: 'feed',
    label: 'Cards per page',
    hint: 'How many cards one numbered page holds. A boost landing between two page loads can move one card across the boundary.',
    public: false,
  },
  {
    key: 'home.per_city',
    type: 'integer',
    default: 5,
    min: 1,
    max: 12,
    group: 'feed',
    label: 'Advertisements per city on the homepage',
    hint: 'How many the homepage shows under each city. Three are on screen at a time and the rest scroll sideways; every card takes you into that city.',
    public: false,
  },
  {
    key: 'gallery.per_row',
    type: 'integer',
    default: 3,
    min: 1,
    max: 6,
    group: 'feed',
    label: 'Advertisements per row in the random gallery',
    hint: 'How many cards stand side by side on the random gallery, at every width, phone included. Three matches the homepage.',
    public: false,
  },
  {
    key: 'gallery.page_size',
    type: 'integer',
    default: 24,
    min: 6,
    max: 60,
    group: 'feed',
    label: 'Advertisements per page in the random gallery',
    hint: 'How many one page of the shuffle holds. A multiple of the row count keeps the last row full.',
    public: false,
  },
  {
    key: 'home.max_cities',
    type: 'integer',
    default: 8,
    min: 3,
    max: 20,
    group: 'feed',
    label: 'Cities on the homepage',
    hint:
      'How many places get a section, busiest first. There are fifty-one regions and a homepage ' +
      'that named them all would be an index rather than an invitation.',
    public: false,
  },
  {
    key: 'location.cross_border_km',
    type: 'integer',
    default: 300,
    min: 0,
    max: 1000,
    group: 'feed',
    label: 'Reach across a state border this far',
    hint:
      'A place page shows its own state at any distance, plus advertisements this close across ' +
      'the border. 0 stops the border being crossed anywhere. A border town can override it.',
    public: false,
  },
  {
    key: 'search.radius_km',
    type: 'integer',
    default: 10,
    min: 1,
    max: 100,
    group: 'feed',
    label: 'Search results reach this far',
    hint:
      'A place searched for from the find sheet lists the advertisements at that place first, ' +
      'then those at other places this close to it, nearest first, each place in its own boost ' +
      'order. An advertisement at a capital reaches as far as that capital’s own range instead. ' +
      'City pages are not affected: they show the whole state.',
    public: false,
  },
  {
    key: 'venues.enabled',
    type: 'boolean',
    default: false,
    group: 'feed',
    label: 'City pages draw the Venues map',
    hint:
      'Off hides the Venues view on every city page, the public venues route answers 404, and ' +
      'the menu and footer link to the external Rub maps site instead. The Rub maps console ' +
      'keeps working either way.',
    public: true,
  },
  {
    key: 'venues.radius_km',
    type: 'integer',
    default: 5,
    min: 1,
    max: 50,
    group: 'feed',
    label: 'A city map opens this wide',
    hint:
      'The Venues view on a city page opens on a ring this many kilometres around the place, ' +
      'and “Back to” returns to it. The reader can still pan out as far as the whole state.',
    public: true,
  },
  {
    key: 'location.suggest_limit',
    type: 'integer',
    default: 12,
    min: 4,
    max: 30,
    group: 'feed',
    label: 'Places offered while typing',
    hint: 'How many suburbs and regions the place box offers at once.',
    public: false,
  },
  {
    key: 'photo.max_per_advertiser',
    type: 'integer',
    default: 50,
    min: 1,
    max: 200,
    group: 'content',
    label: 'Photos per advertiser',
    hint: 'Lowering this never deletes anything; it stops the next upload.',
    public: true,
  },
  {
    key: 'photo.max_original_mb',
    type: 'integer',
    default: 12,
    min: 1,
    max: 50,
    group: 'content',
    label: 'Largest original, in MB',
    hint: 'The file somebody uploads, before it is cropped and re-encoded.',
    public: true,
  },
  {
    key: 'photo.min_short_edge',
    type: 'integer',
    default: 900,
    min: 200,
    max: 4000,
    group: 'content',
    label: 'Smallest original, short edge in pixels',
    hint: 'Below this the variant ladder cannot be produced without upscaling. 900 is the ladder’s own largest.',
    public: true,
  },
  {
    key: 'photo.temporary_max',
    type: 'integer',
    default: 3,
    min: 1,
    max: 10,
    group: 'content',
    label: 'Temporary photographs at a time',
    hint: 'How many photographs an advertiser may show in the temporary set. They are separate from the gallery and do not count against its limit.',
    public: true,
  },
  {
    key: 'photo.temporary_days',
    type: 'integer',
    default: 3,
    min: 1,
    max: 30,
    group: 'content',
    label: 'Days a temporary photograph stays up',
    hint: 'Counted from the moment it is published. Changing it moves photographs already up only when they are next published.',
    public: true,
  },
  {
    key: 'schedule.max_blocks_per_day',
    type: 'integer',
    default: 3,
    min: 1,
    max: 6,
    group: 'content',
    label: 'Blocks of hours per day',
    hint: 'More than three fragments a day stops reading as a shape at 320px, which is what the planner is for.',
    public: false,
  },
  {
    key: 'profile.max_tours',
    type: 'integer',
    default: 2,
    min: 1,
    max: 10,
    group: 'content',
    label: 'Tours announced at once',
    hint: 'The block leads the profile. A year of tours is a timetable nobody reads.',
    public: true,
  },
  {
    key: 'profile.max_rates',
    type: 'integer',
    default: 12,
    min: 1,
    max: 12,
    group: 'content',
    label: 'Rows on a rate sheet',
    hint: 'Cannot exceed 12: the request schema refuses a longer sheet first.',
    public: false,
  },
  {
    key: 'admin.account_page_size',
    type: 'integer',
    default: 50,
    min: 10,
    max: 200,
    group: 'content',
    label: 'Accounts listed in the console',
    hint: 'Past this, search is how you reach the rest.',
    public: false,
  },
  {
    key: 'admin.photo_page_size',
    type: 'integer',
    default: 48,
    min: 12,
    max: 120,
    group: 'content',
    label: 'Photographs per page in the console',
    hint: 'One page of the Photos section, across every account.',
    public: false,
  },
  {
    key: 'boost.reset_utc_seconds',
    type: 'integer',
    default: 64_800,
    min: 0,
    max: 86_399,
    group: 'boost',
    label: 'Reset boundary, seconds past midnight UTC',
    hint: '64800 is 18:00 UTC, which is 04:00 AEST. Read the warning above before moving it.',
    public: false,
  },
  {
    key: 'boost.per_day',
    type: 'integer',
    default: 1,
    min: 1,
    max: 10,
    group: 'boost',
    label: 'Boosts each ad profile gets each day',
    hint: 'Each listing gets this many, so an account with both profiles on gets twice this. Agency-managed advertisers included.',
    public: false,
  },
  {
    key: 'punter.registration_open',
    type: 'boolean',
    default: true,
    group: 'punters',
    label: 'Anyone may register',
    hint: 'Off closes /join. Existing punters keep their accounts and can still sign in.',
    public: true,
  },
  {
    key: 'punter.sign_in_enabled',
    type: 'boolean',
    default: true,
    group: 'punters',
    label: 'Punters may sign in',
    hint: 'Off refuses every punter sign-in and every punter route. Sessions are kept, and work again when this is back on.',
    public: true,
  },
  {
    key: 'punter.require_phone',
    type: 'boolean',
    default: true,
    group: 'punters',
    label: 'A phone number is required to register',
    hint: 'It is held unverified, unique among punters.',
    public: true,
  },
  {
    key: 'punter.code_minutes',
    type: 'integer',
    default: 15,
    min: 5,
    max: 120,
    group: 'punters',
    label: 'Verification code lasts, minutes',
    hint: 'The six-digit code mailed at registration. A new one replaces it.',
    public: false,
  },
  {
    key: 'punter.code_attempts',
    type: 'integer',
    default: 5,
    min: 1,
    max: 20,
    group: 'punters',
    label: 'Wrong codes before a code is void',
    hint: 'After this many, the punter has to ask for a new one.',
    public: false,
  },
  {
    key: 'punter.unverified_purge_days',
    type: 'integer',
    default: 14,
    min: 1,
    max: 365,
    group: 'punters',
    label: 'Remove never-verified registrations after, days',
    hint: 'The nightly job deletes a registration whose code was never entered, which frees its address.',
    public: false,
  },
  {
    key: 'punter.reset_enabled',
    type: 'boolean',
    default: true,
    group: 'punters',
    label: 'Punters may reset a forgotten password by email',
    hint: 'Off leaves the admin console as the only way back in.',
    public: true,
  },
  {
    key: 'auth.reset_enabled',
    type: 'boolean',
    default: true,
    group: 'punters',
    label: 'Advertisers and agencies may reset a forgotten password by email',
    hint: 'Off returns to the reissued invite, handed over in person. Agency-created advertisers never sign in either way.',
    public: true,
  },
  {
    key: 'auth.reset_minutes',
    type: 'integer',
    default: 30,
    min: 10,
    max: 1440,
    group: 'punters',
    label: 'A reset link lasts, minutes',
    hint: 'For both kinds of account.',
    public: false,
  },
  {
    key: 'mail.per_address_per_hour',
    type: 'integer',
    default: 3,
    min: 1,
    max: 20,
    group: 'punters',
    label: 'Mails to one address each hour',
    hint: 'Codes and reset links together. Stops the form being used to flood somebody else.',
    public: false,
  },
  {
    key: 'lists.enabled',
    type: 'boolean',
    default: true,
    group: 'punters',
    label: 'Punters may save advertisers to lists',
    hint: 'Off hides saving and every list. Nothing is deleted.',
    public: true,
  },
  {
    key: 'lists.sharing_enabled',
    type: 'boolean',
    default: true,
    group: 'punters',
    label: 'Lists may be shared by link',
    hint: 'Off makes every shared link a 404 at once, without deleting the links: turned back on, they work again.',
    public: true,
  },
  {
    key: 'lists.max_per_punter',
    type: 'integer',
    default: 20,
    min: 1,
    max: 200,
    group: 'punters',
    label: 'Lists each punter may keep',
    hint: 'A per-punter limit in the account editor replaces this for one punter.',
    public: true,
  },
  {
    key: 'lists.max_items',
    type: 'integer',
    default: 200,
    min: 1,
    max: 2000,
    group: 'punters',
    label: 'Advertisers in one list',
    hint: 'A per-punter limit in the account editor replaces this for one punter.',
    public: true,
  },
  {
    key: 'lists.saver_visible_enabled',
    type: 'boolean',
    default: true,
    group: 'punters',
    label: 'A paused advertiser may stay visible to the people who saved them',
    hint: 'Off makes a paused advertiser grey in every list, whatever they chose.',
    public: true,
  },
  {
    key: 'lists.show_saved_count',
    type: 'boolean',
    default: true,
    group: 'punters',
    label: 'Advertisers see how many people saved them',
    hint: 'A count only. Nobody but the punter ever sees who saved somebody, or a list name.',
    public: false,
  },
] as const satisfies readonly SettingDefinition[];

export type SettingKey = (typeof SETTINGS)[number]['key'];

/**
 * The same list, widened. `as const satisfies` above keeps each entry's literal type, which is
 * what gives `SettingKey` its union — but it also means `min` exists only on the integer entries.
 * Anything walking the list uniformly wants this view.
 */
export const SETTING_LIST: readonly SettingDefinition[] = SETTINGS;

export const SETTING_KEYS = SETTINGS.map((setting) => setting.key) as readonly SettingKey[];

const BY_KEY = new Map<string, SettingDefinition>(SETTINGS.map((s) => [s.key, s]));

export const settingDefinition = (key: string): SettingDefinition | undefined => BY_KEY.get(key);

export type SettingValues = Record<SettingKey, number | boolean>;

export function defaultSettings(): SettingValues {
  const values = {} as SettingValues;
  for (const setting of SETTINGS) values[setting.key] = setting.default;
  return values;
}

/**
 * Reads a stored value back into its type. Everything is TEXT in the column, because one column
 * holding two types is simpler than two nullable ones, and the definition already says which.
 * Anything unparseable falls back to the default rather than throwing: a corrupt row must not be
 * able to take the console down.
 */
export function parseSetting(definition: SettingDefinition, raw: string): number | boolean {
  if (definition.type === 'boolean') return raw === 'true' || raw === '1';
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return definition.default;
  if (definition.min !== undefined && parsed < definition.min) return definition.default;
  if (definition.max !== undefined && parsed > definition.max) return definition.default;
  return parsed;
}

export const serialiseSetting = (value: number | boolean): string => String(value);

/** Null when the value is acceptable, otherwise the reason it is not. */
export function validateSetting(
  definition: SettingDefinition,
  value: unknown,
): string | null {
  if (definition.type === 'boolean') {
    return typeof value === 'boolean' ? null : 'expected true or false';
  }
  if (typeof value !== 'number' || !Number.isInteger(value)) return 'expected a whole number';
  if (definition.min !== undefined && value < definition.min) return `minimum is ${definition.min}`;
  if (definition.max !== undefined && value > definition.max) return `maximum is ${definition.max}`;
  return null;
}

/** Typed accessors, so a caller cannot read a boolean setting as a number by accident. */
export const settingNumber = (values: SettingValues, key: SettingKey): number => {
  const value = values[key];
  return typeof value === 'number' ? value : Number(value);
};

export const settingBoolean = (values: SettingValues, key: SettingKey): boolean => {
  const value = values[key];
  return typeof value === 'boolean' ? value : Boolean(value);
};

/**
 * The widest value a key will ever take, for the one place a runtime value cannot reach: a zod
 * schema, built once at module load and published as the OpenAPI document. The spec states this
 * ceiling and the handler re-checks the value actually configured, so the document stays true
 * without pretending the live limit is fixed.
 */
export function settingCeiling(key: SettingKey): number {
  const definition = BY_KEY.get(key);
  const max = definition?.max;
  if (typeof max !== 'number') {
    throw new Error(`Setting "${key}" has no ceiling; it cannot bound a schema.`);
  }
  return max;
}
